import React, { useEffect, useMemo, useState } from "react";
import Seo from "@/components/Seo";
import { SEO } from "@/lib/seoConfig";
import { Link } from "react-router-dom";
import { Section, Kicker, SerifTitle } from "@/components/Section";
import { EditorialImage } from "@/components/EditorialImage";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { PublicAPI } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";
import { JOURNAL_FIELD_NOTES_COPY } from "@/lib/data";
import { cn } from "@/lib/utils";

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
    } catch {
        return "";
    }
};

// Heuristic: a post belongs to "Field Notes" if its category contains
// keywords related to travel / process / location / observation. Until the
// admin journal editor adds a dedicated category, this lets clients filter
// existing posts cleanly.
const FIELD_NOTE_KEYWORDS = [
    "field note", "field notes", "travel", "location", "process",
    "behind", "observation", "light study", "archive",
];

const isFieldNote = (post) => {
    if (!post) return false;
    const cat = (post.category || "").toLowerCase();
    if (!cat) return false;
    return FIELD_NOTE_KEYWORDS.some((k) => cat.includes(k));
};

const CATEGORIES = ["All", "Field Notes"];

export default function Journal() {
    const [posts, setPosts] = useState(null);
    const [active, setActive] = useState("All");

    useEffect(() => {
        let cancelled = false;
        PublicAPI.listJournal()
            .then((data) => { if (!cancelled) setPosts(data); })
            .catch(() => { if (!cancelled) setPosts([]); });
        return () => { cancelled = true; };
    }, []);

    const filtered = useMemo(() => {
        if (!posts) return null;
        if (active === "All") return posts;
        if (active === "Field Notes") return posts.filter(isFieldNote);
        return posts;
    }, [posts, active]);

    return (
        <div className="pt-24 sm:pt-28">
            <Seo {...SEO.journal} />
            <Section spacing="tight">
                <Kicker>Journal</Kicker>
                <SerifTitle className="mt-3">Notes from the studio.</SerifTitle>
                <p className="mt-5 max-w-[64ch] text-sm leading-[1.85] text-muted-foreground">
                    Short essays on photography, branding, and the quiet craft of being seen.
                </p>

                {/* Field Notes category intro */}
                <div className="mt-7 max-w-[64ch] border-l-2 border-[hsl(var(--lux-brass))]/60 pl-5" data-testid="journal-field-notes-intro">
                    <div className="kicker text-[hsl(var(--lux-oxblood))]">Field Notes</div>
                    <p className="mt-2 text-sm leading-[1.85] text-muted-foreground">
                        {JOURNAL_FIELD_NOTES_COPY}
                    </p>
                </div>

                {/* Category chips */}
                <div className="mt-7 flex flex-wrap gap-2 text-[11px] tracking-[0.18em] uppercase" data-testid="journal-category-chips">
                    {CATEGORIES.map((c) => (
                        <button
                            key={c}
                            onClick={() => setActive(c)}
                            data-testid={`journal-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={cn(
                                "px-3.5 h-9 inline-flex items-center rounded-full border transition-colors",
                                active === c
                                    ? "border-[hsl(var(--lux-oxblood))] text-[hsl(var(--lux-oxblood))] bg-[hsl(var(--lux-oxblood))]/[0.06]"
                                    : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/40"
                            )}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </Section>

            <Section spacing="normal" data-testid="journal-index">
                {filtered === null ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="aspect-[4/3] w-full" />)}
                    </div>
                ) : filtered.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        {active === "Field Notes"
                            ? "No Field Notes published yet — new entries are being prepared."
                            : "No posts yet — the journal is being prepared."}
                    </p>
                ) : (
                    <div className="space-y-12">
                        {filtered.map((p, i) => (
                            <article key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center border-t border-border pt-12 first:border-t-0 first:pt-0">
                                <Link to={`/journal/${p.slug}`} className={`md:col-span-7 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                                    <EditorialImage src={p.cover_image} alt={p.title} ratio={16 / 11} />
                                </Link>
                                <div className="md:col-span-5">
                                    <div className="kicker">
                                        {isFieldNote(p) ? "Field Notes" : (p.category || "Essay")} · {fmt(p.created_at)}
                                    </div>
                                    <h2 className="font-serif-display text-3xl mt-3 leading-tight">
                                        <Link to={`/journal/${p.slug}`} className="hover:underline decoration-[hsl(var(--lux-brass))]">{p.title}</Link>
                                    </h2>
                                    <Separator className="my-4 bg-[hsl(var(--lux-brass))]/40 w-8" />
                                    <p className="text-sm leading-[1.85] text-muted-foreground">{p.excerpt}</p>
                                    <Link to={`/journal/${p.slug}`} className="mt-5 inline-flex items-center text-xs tracking-[0.18em] uppercase lux-underline">
                                        Read essay <ArrowUpRight className="ml-2 h-4 w-4" />
                                    </Link>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </Section>
        </div>
    );
}
